import { useReducer, useRef, useEffect, useCallback, useMemo } from 'react';
import { StreamingPlugin } from '../core/streaming';
const initialState = {
    connectionState: 'disconnected', iceState: 'new', streams: [],
    mediaStream: null, dataChannel: null, stats: null, error: null, activeStreamId: null, plugin: null,
};
function streamingReducer(state, action) {
    switch (action.type) {
        case 'SET_CONNECTION_STATE': return { ...state, connectionState: action.payload };
        case 'SET_ICE_STATE': return { ...state, iceState: action.payload };
        case 'SET_STREAMS': return { ...state, streams: action.payload };
        case 'SET_MEDIA_STREAM': return { ...state, mediaStream: action.payload };
        case 'SET_STATS': return { ...state, stats: action.payload };
        case 'SET_ERROR': return { ...state, error: action.payload };
        case 'SET_ACTIVE_STREAM': return { ...state, activeStreamId: action.payload };
        case 'SET_PLUGIN': return { ...state, plugin: action.payload };
        case 'SET_DATA_CHANNEL': return { ...state, dataChannel: action.payload };
        case 'RESET': return { ...initialState };
        default: return state;
    }
}
export function useJanusStreaming(config) {
    const [state, dispatch] = useReducer(streamingReducer, initialState);
    const pluginRef = useRef(null);
    const logsRef = useRef([]);
    const abortRef = useRef(null);
    useEffect(() => {
        const plugin = new StreamingPlugin(config);
        pluginRef.current = plugin;
        dispatch({ type: 'SET_PLUGIN', payload: plugin });
        const unsubs = [
            plugin.on('stateChange', (s) => dispatch({ type: 'SET_CONNECTION_STATE', payload: s })),
            plugin.on('iceStateChange', (s) => dispatch({ type: 'SET_ICE_STATE', payload: s })),
            plugin.on('streams', (s) => dispatch({ type: 'SET_STREAMS', payload: s })),
            plugin.on('stream', (s) => dispatch({ type: 'SET_MEDIA_STREAM', payload: s })),
            plugin.on('stats', (s) => dispatch({ type: 'SET_STATS', payload: s })),
            plugin.on('error', (e) => dispatch({ type: 'SET_ERROR', payload: e })),
            plugin.on('dataChannelOpen', (ch) => dispatch({ type: 'SET_DATA_CHANNEL', payload: ch })),
            plugin.on('dataChannelClose', () => dispatch({ type: 'SET_DATA_CHANNEL', payload: null })),
            plugin.on('log', (entry) => { logsRef.current = [...logsRef.current.slice(-199), entry]; }),
        ];
        return () => {
            unsubs.forEach((u) => u());
            abortRef.current?.abort();
            plugin.disconnect().catch(() => { });
            plugin.dispose();
            pluginRef.current = null;
            dispatch({ type: 'RESET' });
        };
        // Config is read once at plugin creation. Only `server` changes recreate the plugin.
        // Other config changes (iceServers, keepalive, etc.) require unmount/remount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config.server]);
    const connect = useCallback(async () => {
        if (!pluginRef.current)
            return;
        abortRef.current = new AbortController();
        dispatch({ type: 'SET_ERROR', payload: null });
        await pluginRef.current.connect(abortRef.current.signal);
    }, []);
    const watch = useCallback(async (options) => {
        if (!pluginRef.current)
            return;
        dispatch({ type: 'SET_ERROR', payload: null });
        dispatch({ type: 'SET_ACTIVE_STREAM', payload: options.id });
        await pluginRef.current.watch({ ...options, signal: abortRef.current?.signal });
    }, []);
    const switchStream = useCallback(async (options) => {
        if (!pluginRef.current)
            return;
        dispatch({ type: 'SET_ERROR', payload: null });
        dispatch({ type: 'SET_ACTIVE_STREAM', payload: options.id });
        await pluginRef.current.switchStream({ ...options, signal: abortRef.current?.signal });
    }, []);
    const stop = useCallback(async () => {
        if (!pluginRef.current)
            return;
        await pluginRef.current.stop();
        dispatch({ type: 'SET_MEDIA_STREAM', payload: null });
        dispatch({ type: 'SET_ACTIVE_STREAM', payload: null });
    }, []);
    const disconnect = useCallback(async () => {
        if (!pluginRef.current)
            return;
        abortRef.current?.abort();
        await pluginRef.current.disconnect();
        dispatch({ type: 'RESET' });
    }, []);
    const clearError = useCallback(() => { dispatch({ type: 'SET_ERROR', payload: null }); }, []);
    const sendData = useCallback((data) => { pluginRef.current?.sendData(data); }, []);
    return useMemo(() => ({
        ...state, logs: logsRef.current,
        connect, watch, switchStream, stop, disconnect, clearError, sendData,
    }), [state, connect, watch, switchStream, stop, disconnect, clearError, sendData]);
}
